import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "react-query";
import { RequestApi } from ".";
import { useAppContext } from "../AppProviders";
import {
  getWorkflowDashboardMock,
  getWorkflowInfoListMock,
  getWorkflowLock,
  getWorkflowThresholdDashboardMock,
} from "../__mocks__/workflowList";
import { postManualRateDoneMock } from "../__mocks__/workflowOps";
import { CacheKeys } from "./Cache";
import { API_URL, isMocksEnabled } from "./helpers";
import {
  ThresholdDashboardEntry,
  WorkflowBase,
  WorkflowDashboardEntry,
  WorkflowInfoApi,
  WorkflowRun,
  WorkflowRunChangeDueDate,
  WorkflowRunRowDataResponse,
  WORKFLOW_STEP_STATUS_ENUM,
} from "./Workflows";
import { ReportOriginType } from "../reports/ReportsHooks";

export function useGetWorkflowInfoList() {
  const { requestApi, cache, refreshCache } = useAppContext();

  const getWorkflowInfoList = async (): Promise<WorkflowInfoApi[]> => {
    if (isMocksEnabled()) {
      return await getWorkflowInfoListMock();
    }
    if (
      cache.current.workflowConfigs?.items &&
      Array.isArray(cache.current.workflowConfigs?.items)
    ) {
      return cache.current.workflowConfigs.items;
    }
    return requestApi
      .get(`${API_URL}/workflowdal/workflow_configs`)
      .then((response) => {
        return response.data;
      })
      .then((items) => {
        const mappedItems = items.map((item: WorkflowBase) => ({
          workflow_config_uuid: item.external_id,
          name: item.name,
        }));
        refreshCache(CacheKeys.worflowConfigs, mappedItems);
        return mappedItems;
      });
  };

  const mutationResult = useMutation(() => getWorkflowInfoList(), {});

  return {
    ...mutationResult,
  };
}

export function useUpdateDashboardThreshold() {
  const { requestApi } = useAppContext();

  const updateDasboardThreshold = async (
    input: ThresholdDashboardEntry
  ): Promise<WorkflowRun> => {
    return requestApi
      .put(`${API_URL}/workflowdal/dashboard_threshold/`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ThresholdDashboardEntry) => updateDasboardThreshold(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateMisconductDueDate() {
  const { requestApi, refreshCache } = useAppContext();

  const updateWorkflow = async (
    input: WorkflowRunChangeDueDate
  ): Promise<string> => {
    return requestApi
      .put(`${API_URL}/misconduct/due_date`, input)
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: WorkflowRunChangeDueDate) => updateWorkflow(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateWorkflow() {
  const { requestApi, refreshCache } = useAppContext();

  const updateWorkflow = async (input: WorkflowRun): Promise<WorkflowRun> => {
    // if (isMocksEnabled()) {
    // }
    return requestApi
      .put(`${API_URL}/workflowdal/workflowruns/${input.external_id}`, input)
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: WorkflowRun) => updateWorkflow(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function usePostManualRateDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
    workflowRunUUID: string;
  }

  const postManualRateDone = async ({
    reportUUID,
    workflowRunUUID,
  }: MutationProps): Promise<string> => {
    if (isMocksEnabled()) {
      return await postManualRateDoneMock(reportUUID);
    }
    return requestApi
      .post(`${API_URL}/post_manual_rate_done?report_uuid=${reportUUID}`)
      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, workflowRunUUID }: MutationProps) =>
      postManualRateDone({ reportUUID, workflowRunUUID }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function usePostManualEditDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
  }

  const postManualEdit = async ({
    reportUUID,
  }: MutationProps): Promise<string> => {
    return requestApi
      .post(`${API_URL}/post_manual_edit_done?report_uuid=${reportUUID}`)
      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID }: MutationProps) => postManualEdit({ reportUUID }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function usePostScrapeDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
    workflowRunUUID: string;
  }

  const postScrapeDone = async ({
    reportUUID,
    workflowRunUUID,
  }: MutationProps): Promise<string> => {
    return requestApi
      .post(`${API_URL}/manual_posts_scrape_done?report_uuid=${reportUUID}`)

      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, workflowRunUUID }: MutationProps) =>
      postScrapeDone({ reportUUID, workflowRunUUID }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useProfileManualRateDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
    workflowRunUUID: string;
  }

  const profileManualRateDone = async ({
    reportUUID,
    workflowRunUUID,
  }: MutationProps): Promise<string> => {
    return requestApi
      .post(`${API_URL}/profile_manual_rate_done?report_uuid=${reportUUID}`)
      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then(async (response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, workflowRunUUID }: MutationProps) =>
      profileManualRateDone({ reportUUID, workflowRunUUID }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function use360WebSearchDone() {
  const { requestApi } = useAppContext();
  interface MutationProps {
    requestUUID: string;
  }
  const setWebSearchDone = async ({
    requestUUID,
  }: MutationProps): Promise<string> => {
    return requestApi
      .post(
        `${API_URL}/three_sixty_web_search_done?request_uuid=${requestUUID}`
      )
      .then(async (response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    ({ requestUUID }: MutationProps) => setWebSearchDone({ requestUUID }),
    {}
  );
  return { ...mutationResult };
}

export function useQaDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
    stepName: string;
  }

  const qaDone = async ({
    reportUUID,
    stepName,
  }: MutationProps): Promise<string> => {
    const AWAIT_TIME = 3000;
    const stepEndpointMap: { [key: string]: string } = {
      ClientQA: "client_qa_step_done",
      InternalQA: "internal_qa_step_done",
      AmendmentQA: "amendment_qa_step_done",
      [WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA]:
        "three_sixty_misconduct_qa_done",
      [WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview]:
        "three_sixty_misconduct_qa_done",
      [WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch]:
        "three_sixty_misconduct_qa_done",
    };
    const stepEndpoint = stepEndpointMap[stepName];
    return requestApi
      .post(`${API_URL}/${stepEndpoint}?report_uuid=${reportUUID}`)
      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then(async (response) => {
        await new Promise((response) => setTimeout(response, AWAIT_TIME));
        return response;
      })
      .then(async (response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, stepName }: MutationProps) =>
      qaDone({ reportUUID, stepName }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useWorkflowLock() {
  const { requestApi } = useAppContext();

  const workflowLock = async (
    workflowRunUUID: string,
    origin?: ReportOriginType
  ): Promise<WorkflowRun> => {
    if (isMocksEnabled()) {
      return await getWorkflowLock(workflowRunUUID);
    }
    let url = `${API_URL}/workflowruns/${workflowRunUUID}/lock`;
    if (origin && origin !== "none") {
      url += `?origin=${origin}`;
    }

    return requestApi.post(url).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(
    ({
      workflowRunUUID,
      origin,
    }: {
      workflowRunUUID: string;
      origin?: ReportOriginType;
    }) => workflowLock(workflowRunUUID, origin),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetWorkflowDashboard() {
  const { requestApi } = useAppContext();

  const getWorkflowDashboard = async (): Promise<WorkflowDashboardEntry[]> => {
    if (isMocksEnabled()) {
      return await getWorkflowDashboardMock(200);
    }

    return requestApi.get(`${API_URL}/workflow/dashboard`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(() => getWorkflowDashboard(), {});

  return {
    ...mutationResult,
  };
}

export function useGetWorkflowThreshold() {
  const { requestApi } = useAppContext();

  const getWorkflowThresholdDashboard = async (): Promise<
    ThresholdDashboardEntry[]
  > => {
    if (isMocksEnabled()) {
      return await getWorkflowThresholdDashboardMock(10);
    }

    return requestApi
      .get(`${API_URL}/workflowdal/dashboard_threshold`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(() => getWorkflowThresholdDashboard(), {});

  return {
    ...mutationResult,
  };
}

export function useCompleteThreeSixtyReview() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  interface MutationProps {
    report_uuid: string;
    skip_three_sixty_qa?: boolean;
  }

  const completeThreeSixtyReview = async ({
    report_uuid,
    skip_three_sixty_qa = false,
  }: MutationProps): Promise<WorkflowRun> => {
    return requestApi
      .post(
        `${API_URL}/three_sixty_plus_review_done?report_uuid=${report_uuid}&skip_three_sixty_qa=${skip_three_sixty_qa}`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (props: MutationProps) => completeThreeSixtyReview(props),
    {
      onSuccess: () => {
        // This will invalidate all workflow run queries (e.g., ["workflowRun", uuid])
        queryClient.invalidateQueries({ queryKey: ["workflowRun"] });
      },
    }
  );

  return {
    ...mutationResult,
  };
}

export function useCompleteSecondaryQA() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  interface MutationProps {
    report_uuid: string;
  }

  const completeSecondaryQA = async ({
    report_uuid,
  }: MutationProps): Promise<WorkflowRun> => {
    return requestApi
      .post(`${API_URL}/three_sixty_qa_step_done?report_uuid=${report_uuid}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (props: MutationProps) => completeSecondaryQA(props),
    {
      onSuccess: () => {
        // Invalidate workflow run queries to refresh lists
        queryClient.invalidateQueries({ queryKey: ["workflowRun"] });
        // Also invalidate the 360 QA list
        queryClient.invalidateQueries({ queryKey: ["threeSixtyQAList"] });
      },
    }
  );

  return {
    ...mutationResult,
  };
}

export function useComplete360Amend() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  interface MutationProps {
    report_uuid: string;
  }

  const complete360Amend = async ({
    report_uuid,
  }: MutationProps): Promise<WorkflowRun> => {
    return requestApi
      .post(`${API_URL}/360_amend_step_done?report_uuid=${report_uuid}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (props: MutationProps) => complete360Amend(props),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["workflowRun"] });
      },
    }
  );

  return {
    ...mutationResult,
  };
}

export function useUnlockWorkflow() {
  const { requestApi, refreshCache } = useAppContext();
  const queryClient = useQueryClient();
  interface MutationProps {
    workflowRunUUID: string;
    success: boolean;
    /** Optional reason for backend logging (e.g. "step_done" for "Lock cleared, step done") */
    reason?: string;
  }

  const unlockWorkflow = async (
    { workflowRunUUID, success, reason }: MutationProps,
    requestApi: RequestApi,
    refreshCache: (key: CacheKeys, value?: any) => void
  ): Promise<WorkflowRun> => {
    const val = success ? "1" : "0";
    let url = `${API_URL}/workflowruns/${workflowRunUUID}/lock?success=${val}`;
    if (reason) {
      url += `&reason=${encodeURIComponent(reason)}`;
    }
    return requestApi.delete(url).then((response) => {
      refreshCache(CacheKeys.reports);
      return response.data;
    });
  };

  const mutationResult = useMutation(
    (props: MutationProps) => unlockWorkflow(props, requestApi, refreshCache),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(["reportsInProcess"]);
        void queryClient.invalidateQueries(["reportInfoListQuery"]);
      },
    }
  );

  return {
    ...mutationResult,
  };
}

export function useArticleManualRateDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
    workflowRunUUID: string;
  }

  const articleManualRateDone = async ({
    reportUUID,
    workflowRunUUID,
  }: MutationProps): Promise<string> => {
    return requestApi
      .post(`${API_URL}/article_manual_rate_done?report_uuid=${reportUUID}`)
      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, workflowRunUUID }: MutationProps) =>
      articleManualRateDone({ reportUUID, workflowRunUUID }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useArticleManualEditDone() {
  const { requestApi, refreshCache } = useAppContext();
  interface MutationProps {
    reportUUID: string;
    workflowRunUUID: string;
  }

  const articleManualEditDone = async ({
    reportUUID,
    workflowRunUUID,
  }: MutationProps): Promise<string> => {
    return requestApi
      .post(`${API_URL}/article_manual_edit_done?report_uuid=${reportUUID}`)
      .then(async (response) => {
        refreshCache(CacheKeys.reports);
        return response;
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, workflowRunUUID }: MutationProps) =>
      articleManualEditDone({ reportUUID, workflowRunUUID }),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useResumeFailedWorkflowRun() {
  const { requestApi } = useAppContext();

  const resumeWorkflowRun = async (workflow_run_uuid: string): Promise<any> => {
    if (isMocksEnabled()) {
      return Promise.resolve({ message: "Mock resumed" });
    }

    return requestApi
      .post(`${API_URL}/workflow_runs/${workflow_run_uuid}/resume`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (workflow_run_uuid: string) => resumeWorkflowRun(workflow_run_uuid),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetSubworkflowData() {
  const { requestApi } = useAppContext();

  const getSubworkflowData = async (
    workflow_run_uuid: string
  ): Promise<WorkflowRun[]> => {
    if (isMocksEnabled()) {
      return Promise.resolve([
        { external_id: "00000000-0000-0000-0000-000000000000", name: "360" },
      ]);
    }
    return requestApi
      .get(`${API_URL}/workflow_runs/${workflow_run_uuid}/children`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (workflow_run_uuid: string) => getSubworkflowData(workflow_run_uuid),
    {}
  );

  return { ...mutationResult };
}

/** Freshness overrides for callers that must not act on a cached row (e.g. the 360 claim prompt). */
export type WorkflowRunByUUIDOptions = Pick<
  UseQueryOptions<
    WorkflowRunRowDataResponse,
    unknown,
    WorkflowRunRowDataResponse,
    (string | undefined)[]
  >,
  "staleTime" | "refetchOnMount"
>;

export function useGetWorkflowRunByUUID(
  workflow_run_uuid: string | undefined,
  options?: WorkflowRunByUUIDOptions
) {
  const { requestApi } = useAppContext();

  return useQuery(
    ["workflowRun", workflow_run_uuid],
    async (): Promise<WorkflowRunRowDataResponse> => {
      if (!workflow_run_uuid) {
        throw new Error("Workflow run UUID is required");
      }
      return requestApi
        .get(`${API_URL}/workflow_run/row_data/${workflow_run_uuid}`)
        .then((response) => response.data);
    },
    {
      enabled: !!workflow_run_uuid,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 2,
      ...options,
    }
  );
}
